import { useId } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CATEGORIES_WITH_SUBTYPES, EFFECTIVE_DATE_CATEGORIES } from '@/types/investor';
import { DOC_VISIBILITY_ROLES } from '@/utils/investor/documentVisibility';
import type { UploadFields, UploadChannel } from '@/services/investorUploadBatch';

export function DocumentUploadFields({ value, onChange, categories, channels,
  disabled = false, lockIdentity = false, lockVisibility = false, lockCategory = false }: {
    value: UploadFields;
    onChange: (value: UploadFields) => void;
    categories: string[];
    channels: UploadChannel[];
    disabled?: boolean;
    lockIdentity?: boolean;
    lockCategory?: boolean;
    lockVisibility?: boolean;
  }) {
  const id = useId();
  const change = (patch: Partial<UploadFields>) => onChange({ ...value, ...patch });
  return <fieldset disabled={disabled} className="min-w-0 space-y-4">
    <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="min-w-0 space-y-1 text-sm"><label htmlFor={`${id}-type`}>Document Type</label>
        <select id={`${id}-type`} value={value.category} disabled={lockIdentity || lockCategory}
          className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 focus:outline-none focus:ring-2 focus:ring-ring"
          onChange={event => change({ category: event.target.value, subType: '', effectiveDate: '' })}>
          <option value="">Choose type</option>
          {[...new Set([...categories, ...((lockIdentity || lockCategory) ? [value.category] : [])])].sort().map(category =>
            <option key={category} value={category}>{category}</option>)}
        </select>
      </div>
      <div className="min-w-0 space-y-1 text-sm"><label htmlFor={`${id}-channel`}>Channel</label>
        <select id={`${id}-channel`} value={value.channel} disabled={lockIdentity || lockCategory}
          className="h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 focus:outline-none focus:ring-2 focus:ring-ring"
          onChange={event => change({ channel: event.target.value as UploadFields['channel'] })}>
          <option value="">Choose channel</option>
          {channels.map(channel => <option key={channel} value={channel}>{channel === 'wholesale' ? 'Wholesale' : 'Correspondent'}</option>)}
        </select>
      </div>
    </div>
    {CATEGORIES_WITH_SUBTYPES.includes(value.category) && <label className="block space-y-1 text-sm">
      Sub-Type *
      <Input value={value.subType} required disabled={lockIdentity}
        onChange={event => change({ subType: event.target.value })} placeholder="e.g., Core, Agency NOO, Jumbo Express" />
    </label>}
    {EFFECTIVE_DATE_CATEGORIES.includes(value.category) && <label className="block space-y-1 text-sm">
      Effective Date
      <Input type="date" value={value.effectiveDate} disabled={lockIdentity} onChange={event => change({ effectiveDate: event.target.value })} />
    </label>}
    <fieldset disabled={lockVisibility || lockIdentity} className="space-y-2 rounded-md border p-3">
      <legend className="px-1 text-sm">Visibility</legend>
      <p className="text-xs text-muted-foreground">Checked roles can see this document. Admins and partners can always see investor documents.</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {['Admin', 'Partner'].map(role => <label key={role} className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked disabled />{role}
        </label>)}
        {DOC_VISIBILITY_ROLES.map(role => <label key={role.value} className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!value.hiddenRoles.includes(role.value)} onChange={event => change({
            hiddenRoles: event.target.checked ? value.hiddenRoles.filter(item => item !== role.value) : [...value.hiddenRoles, role.value]
          })} />{role.label}
        </label>)}
      </div>
    </fieldset>
    <fieldset disabled={lockVisibility || lockIdentity} className="rounded-md border p-3">
      <legend className="px-1 text-sm">Linked Tabs</legend>
      <div className="flex flex-wrap gap-4">
        {(['processing', 'closing'] as const).map(tab => <label key={tab} className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={value.linkedTabs.includes(tab)} onChange={event => change({
            linkedTabs: event.target.checked ? [...value.linkedTabs, tab] : value.linkedTabs.filter(item => item !== tab)
          })} />{tab === 'processing' ? 'Processing' : 'Closing'}
        </label>)}
      </div>
    </fieldset>
    <label className="block space-y-1 text-sm">Notes (Optional)
      <Textarea value={value.notes} disabled={lockIdentity} onChange={event => change({ notes: event.target.value })} rows={2} />
    </label>
  </fieldset>;
}
